import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, ArrowRight, Users, Clock, Target, 
  Shield, Briefcase, HardHat, Wrench, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = '/api';

const challenges = [
  { icon: Clock, title: 'Urgent Vacancies', desc: 'Need people fast without compromising on quality?' },
  { icon: Target, title: 'Hard-to-Fill Roles', desc: 'Struggling to find specialist skills in a tight market?' },
  { icon: Users, title: 'High Turnover', desc: 'Tired of unsuitable candidates that don\'t last?' },
  { icon: Shield, title: 'Quality Concerns', desc: 'Want better screening without the time investment?' },
];

const roleCategories = [
  { title: 'Management & Supervision', roles: ['Site Managers', 'Project Managers', 'Supervisors', 'Forepersons', 'Team Leaders'] },
  { title: 'Engineering', roles: ['Project Engineers', 'Civil Engineers', 'Structural Engineers', 'Estimators', 'Design Engineers'] },
  { title: 'Trades', roles: ['Electricians', 'Plumbers', 'Carpenters', 'Welders', 'Fitters'] },
  { title: 'Site & Support', roles: ['Contract Administrators', 'HSE/Safety Officers', 'Project Coordinators', 'QA/QC Staff', 'Office Support'] },
  { title: 'Industrial', roles: ['Maintenance Staff', 'Machine Operators', 'Warehouse Workers', 'Production Staff', 'Logistics Coordinators'] },
];

const solutions = [
  { icon: Briefcase, title: 'Permanent Recruitment', desc: 'Find committed team members for long-term positions. We focus on quality matches that last.' },
  { icon: Clock, title: 'Contract & Temporary', desc: 'Flexible workforce solutions for project peaks and short-term needs. Fast deployment when you need it.' },
  { icon: HardHat, title: 'Trade & Blue-Collar', desc: 'Qualified tradespeople and site-ready workers, properly vetted and work-ready.' },
  { icon: Wrench, title: 'Engineering & Technical', desc: 'Engineers, estimators and technical specialists with the qualifications you need.' },
  { icon: FileText, title: 'Candidate Screening', desc: 'Thorough vetting, reference checks and qualification verification. Save your time.' },
  { icon: Users, title: 'Volume Hiring', desc: 'Scaled recruitment support for project ramp-ups and growth phases.' },
];

const benefits = [
  'Industry-specific recruitment expertise',
  'Fast shortlist turnaround times',
  'Thorough candidate screening and vetting',
  'Transparent communication throughout',
  'Flexible engagement models',
  'Post-placement support and follow-up',
];

export default function Employers() {
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    email: '',
    phone: '',
    hiring_need: '',
    location: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/request-staff`, formData);
      toast.success(response.data.message);
      setFormData({
        full_name: '',
        company_name: '',
        email: '',
        phone: '',
        hiring_need: '',
        location: '',
        message: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="employers-page">
      <SEO page="employers" />
      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-4">For Employers</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase mb-6">
              Find The Right People, Without The Hassle
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mb-8">
              Specialist recruitment for construction, engineering, trades and industrial businesses. Quality candidates, fast turnaround, no time wasted.
            </p>
            <a href="#request-staff">
              <Button className="bg-[#F5A623] hover:bg-[#D98E15] text-slate-900 font-bold py-6 px-8 uppercase tracking-wide text-base">
                Request Staff Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Challenges */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Hiring Challenges</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase">
              Problems We Solve
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {challenges.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-slate-50 p-8 border-l-4 border-[#1A8B9D]"
              >
                <item.icon className="w-10 h-10 text-[#1A8B9D] mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles We Recruit */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Roles We Recruit</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase">
              Positions We Fill
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {roleCategories.map((category, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 border border-slate-200"
              >
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-4 pb-4 border-b border-slate-200">
                  {category.title}
                </h3>
                <ul className="space-y-2">
                  {category.roles.map((role, rIdx) => (
                    <li key={rIdx} className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-4 h-4 text-[#1A8B9D]" />
                      {role}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Our Solutions</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase">
              Recruitment Services
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((solution, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-slate-50 p-8 border border-slate-200 hover:border-[#1A8B9D]/50 hover:shadow-lg transition-all"
              >
                <solution.icon className="w-10 h-10 text-[#1A8B9D] mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-3">{solution.title}</h3>
                <p className="text-slate-600 text-sm">{solution.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-[#1A8B9D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-teal-200 font-semibold uppercase tracking-wider mb-3">Why Choose Vulcan</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-6">
                Partner With Specialists
              </h2>
              <p className="text-teal-100 text-lg leading-relaxed mb-8">
                We're not generalists trying to fill every role under the sun. We specialise in construction, engineering and industrial recruitment – which means we understand your industry and can deliver candidates that actually fit.
              </p>
            </div>
            <div className="space-y-4">
              {benefits.map((benefit, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-4 bg-white/10 backdrop-blur p-4"
                >
                  <CheckCircle className="w-5 h-5 text-[#F5A623] flex-shrink-0" />
                  <p className="text-white font-medium">{benefit}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Request Staff Form */}
      <section id="request-staff" className="py-20 md:py-28 bg-slate-50 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Get Started</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase mb-4">
              Request Staff
            </h2>
            <p className="text-slate-600">
              Tell us about your hiring needs and we'll be in touch within 24 hours.
            </p>
          </div>
          
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="bg-white p-8 md:p-12 border border-slate-200 shadow-sm"
            data-testid="request-staff-form"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                <Input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-300 focus:border-[#1A8B9D]"
                  placeholder="Your full name"
                  data-testid="staff-form-name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Company Name *</label>
                <Input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-300 focus:border-[#1A8B9D]"
                  placeholder="Your company"
                  data-testid="staff-form-company"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-300 focus:border-[#1A8B9D]"
                  placeholder="email@company.com"
                  data-testid="staff-form-email"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Phone *</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-300 focus:border-[#1A8B9D]"
                  placeholder="+61 xxx xxx xxx"
                  data-testid="staff-form-phone"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Hiring Need / Role Type *</label>
                <Input
                  type="text"
                  name="hiring_need"
                  value={formData.hiring_need}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-300 focus:border-[#1A8B9D]"
                  placeholder="e.g., Site Manager, Electrician"
                  data-testid="staff-form-need"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Location *</label>
                <Input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-300 focus:border-[#1A8B9D]"
                  placeholder="e.g., Melbourne, VIC"
                  data-testid="staff-form-location"
                />
              </div>
            </div>
            <div className="mb-8">
              <label className="block text-sm font-semibold text-slate-700 mb-2">Additional Details</label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="min-h-[120px] border-slate-300 focus:border-[#1A8B9D]"
                placeholder="Tell us more about your requirements..."
                data-testid="staff-form-message"
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold py-4 uppercase tracking-wide text-base"
              data-testid="staff-form-submit"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
